/* ========================================================================
   Tab Collection Slider
   ======================================================================== */
(function () {
  'use strict';

  /* ---------- Custom element: tcs-slider ---------- */
  if (!customElements.get('tcs-slider')) {
    class TcsSlider extends HTMLElement {
      constructor() {
        super();
        this.track = null;
        this.prevBtn = null;
        this.nextBtn = null;
        this._autoplayTimer = null;
        this._isUserInteracting = false;
        this._onScroll = this._onScroll.bind(this);
        this._onResize = this._onResize.bind(this);
        this._onPointerDown = this._onPointerDown.bind(this);
        this._onPointerUp = this._onPointerUp.bind(this);
        this._onMouseEnter = this._onMouseEnter.bind(this);
        this._onMouseLeave = this._onMouseLeave.bind(this);
        this._onFocusIn = this._onFocusIn.bind(this);
        this._onFocusOut = this._onFocusOut.bind(this);
        this._onVisibilityChange = this._onVisibilityChange.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector('[data-slider-track]');
        this.prevBtn = this.querySelector('[data-arrow="prev"]');
        this.nextBtn = this.querySelector('[data-arrow="next"]');
        if (!this.track) return;

        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.autoplayInterval = Math.max(1, parseInt(this.dataset.autoplayInterval, 10) || 4) * 1000;
        this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
        this.shouldLoop = this.dataset.loop !== 'false';

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => {
            this._isUserInteracting = true;
            this.scrollByPage(-1);
            this.restartAutoplay();
          });
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => {
            this._isUserInteracting = true;
            this.scrollByPage(1);
            this.restartAutoplay();
          });
        }

        this.track.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });

        if (this.autoplayEnabled) {
          this.track.addEventListener('pointerdown', this._onPointerDown, { passive: true });
          this.track.addEventListener('pointerup', this._onPointerUp, { passive: true });
          this.track.addEventListener('touchstart', this._onPointerDown, { passive: true });
          this.track.addEventListener('touchend', this._onPointerUp, { passive: true });

          if (this.pauseOnHover) {
            this.addEventListener('mouseenter', this._onMouseEnter);
            this.addEventListener('mouseleave', this._onMouseLeave);
            this.addEventListener('focusin', this._onFocusIn);
            this.addEventListener('focusout', this._onFocusOut);
          }

          document.addEventListener('visibilitychange', this._onVisibilityChange);

          // Only autoplay when this slider's panel is the active one
          this._observePanelVisibility();
          this.maybeStartAutoplay();
        }

        // Initial state — defer so layout settles
        requestAnimationFrame(() => this.updateArrowState());
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onResize);
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        this.stopAutoplay();
      }

      _onPointerDown() {
        this._isUserInteracting = true;
        this.stopAutoplay();
      }

      _onPointerUp() {
        this._isUserInteracting = false;
        this.maybeStartAutoplay();
      }

      _onMouseEnter() { this.stopAutoplay(); }
      _onMouseLeave() {
        if (!this._isUserInteracting) this.maybeStartAutoplay();
      }
      _onFocusIn() { this.stopAutoplay(); }
      _onFocusOut() {
        if (!this.contains(document.activeElement)) this.maybeStartAutoplay();
      }
      _onVisibilityChange() {
        if (document.hidden) this.stopAutoplay();
        else this.maybeStartAutoplay();
      }

      _isInActivePanel() {
        const panel = this.closest('.tcs__panel');
        return !panel || panel.classList.contains('is-active');
      }

      _observePanelVisibility() {
        const panel = this.closest('.tcs__panel');
        if (!panel || typeof MutationObserver === 'undefined') return;
        this._panelObserver = new MutationObserver(() => {
          if (this._isInActivePanel()) this.maybeStartAutoplay();
          else this.stopAutoplay();
        });
        this._panelObserver.observe(panel, { attributes: true, attributeFilter: ['class'] });
      }

      maybeStartAutoplay() {
        if (!this.autoplayEnabled) return;
        if (!this._isInActivePanel()) return;
        if (document.hidden) return;
        this.stopAutoplay();
        this._autoplayTimer = setInterval(() => this.autoplayTick(), this.autoplayInterval);
      }

      stopAutoplay() {
        if (this._autoplayTimer) {
          clearInterval(this._autoplayTimer);
          this._autoplayTimer = null;
        }
      }

      restartAutoplay() {
        this.stopAutoplay();
        // small delay so user can see the move before next tick
        setTimeout(() => this.maybeStartAutoplay(), 200);
      }

      autoplayTick() {
        if (!this.track) return;
        const max = this.track.scrollWidth - this.track.clientWidth - 1;
        const atEnd = this.track.scrollLeft >= max;
        if (atEnd) {
          if (this.shouldLoop) {
            this.track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            this.stopAutoplay();
          }
          return;
        }
        this.scrollByPage(1);
      }

      _onScroll() {
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(() => this.updateArrowState());
      }

      _onResize() {
        if (this._resizeTimer) clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this.updateArrowState(), 120);
      }

      getStep() {
        const firstSlide = this.track.querySelector('.tcs__slide');
        if (!firstSlide) return this.track.clientWidth;
        const styles = window.getComputedStyle(this.track);
        const gap = parseInt(styles.columnGap || styles.gap || '0', 10) || 0;
        const slideWidth = firstSlide.getBoundingClientRect().width;
        return slideWidth + gap;
      }

      scrollByPage(direction) {
        const step = this.getStep();
        const visibleCount = Math.max(1, Math.round(this.track.clientWidth / step));
        const distance = step * visibleCount * direction;
        this.track.scrollBy({ left: distance, behavior: 'smooth' });
      }

      updateArrowState() {
        if (!this.track) return;
        const max = this.track.scrollWidth - this.track.clientWidth - 1;
        const x = this.track.scrollLeft;
        const isOverflowing = this.track.scrollWidth > this.track.clientWidth + 1;

        if (this.prevBtn) {
          this.prevBtn.disabled = !isOverflowing || x <= 0;
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = !isOverflowing || x >= max;
        }
      }
    }

    customElements.define('tcs-slider', TcsSlider);
  }

  /* ---------- Tab switching ---------- */
  function initTabs(section) {
    const tabs = section.querySelectorAll('.tcs__tab');
    const panels = section.querySelectorAll('.tcs__panel');
    if (!tabs.length || !panels.length) return;

    function activate(targetId, focusTab) {
      tabs.forEach((tab) => {
        const isActive = tab.getAttribute('data-tab-target') === targetId;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive && focusTab) tab.focus();
      });

      panels.forEach((panel) => {
        const isActive = panel.id === targetId;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          // Reset slider scroll position and refresh arrow state
          const sliderEl = panel.querySelector('tcs-slider');
          const track = panel.querySelector('[data-slider-track]');
          if (track) track.scrollLeft = 0;
          if (sliderEl && typeof sliderEl.updateArrowState === 'function') {
            requestAnimationFrame(() => sliderEl.updateArrowState());
          }
        }
      });
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        activate(tab.getAttribute('data-tab-target'), false);
      });

      tab.addEventListener('keydown', (event) => {
        const tabsArr = Array.from(tabs);
        const idx = tabsArr.indexOf(tab);
        let nextIdx = null;

        if (event.key === 'ArrowRight') nextIdx = (idx + 1) % tabsArr.length;
        else if (event.key === 'ArrowLeft') nextIdx = (idx - 1 + tabsArr.length) % tabsArr.length;
        else if (event.key === 'Home') nextIdx = 0;
        else if (event.key === 'End') nextIdx = tabsArr.length - 1;

        if (nextIdx !== null) {
          event.preventDefault();
          const target = tabsArr[nextIdx].getAttribute('data-tab-target');
          activate(target, true);
        }
      });
    });
  }

  function init() {
    document.querySelectorAll('.tcs').forEach((section) => {
      if (section.dataset.tcsInit === '1') return;
      section.dataset.tcsInit = '1';
      initTabs(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when sections load/update in the Shopify theme editor
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector('.tcs')) {
      init();
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    if (!block) return;
    // If a tab block is selected in the editor, activate its panel
    const tab = block.matches && block.matches('.tcs__tab') ? block : block.querySelector && block.querySelector('.tcs__tab');
    if (tab) {
      const target = tab.getAttribute('data-tab-target');
      const section = tab.closest('.tcs');
      if (section && target) {
        section.querySelectorAll('.tcs__tab').forEach((t) => {
          const active = t.getAttribute('data-tab-target') === target;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        section.querySelectorAll('.tcs__panel').forEach((p) => {
          p.classList.toggle('is-active', p.id === target);
          p.setAttribute('aria-hidden', p.id === target ? 'false' : 'true');
        });
      }
    }
  });
})();
